import React, {
  useState,
  useContext,
  createContext,
  useEffect,
  useMemo,
  useCallback,
  useRef,
  useSyncExternalStore,
} from "react";
import { useApolloClient, useMutation } from "@apollo/client";
import { useRouter } from "next/router";
import * as authQueries from "utils/authQueries";
import RedirectingOverlay from "components/Core/Auth/RedirectingOverlay";
import { getFragmentData, serverGql } from "__generated__/serverGql";
import type { MutationTuple } from "@apollo/client";
import type { ResultOf, VariablesOf } from "@graphql-typed-document-node/core";
import type { FragmentType } from "__generated__/serverGql";
import { logAsyncCatch } from "./asyncVoidHandler";
import {
  getAuthenticatedRedirectPath,
  getAuthenticatedRedirectNextPath,
  getIsProtectedPath,
} from "./authRoutePolicy";
import tokenService, {
  AUTH_STATUS_CHANGE_EVENT,
  getIsExpectedAuthRefreshError,
} from "./TokenService";
import { LocalStorageKey } from "./localStorage";
import { clearCurrentUser } from "./sentryContext";
import { clearCurrentUser as clearUserbackCurrentUser } from "./userbackContext";
import serverClient from "./apollo/serverClient";
import { setSegmentBrandContext, trackEvent } from "./segment";
import { AppMode, getAppModeForPath } from "./appMode";
import { assertPresence } from "./assertions";

type AuthSnapshot = {
  /** Current auth state for this snapshot, including the route-policy default before hydration. */
  isAuthenticated: boolean;
  /** Whether auth state has been read from the client-side `TokenService`. */
  isReady: boolean;
};

/**
 * Bridges `TokenService` into React while preserving our SSR hydration contract.
 *
 * The server snapshot cannot know the user's actual token state, so
 * `AuthProvider` passes a route-policy default: protected routes render as
 * authenticated and public routes render as unauthenticated. That lets
 * protected pages optimistically render their authenticated shell on the server,
 * then the client snapshot replaces that value with `TokenService` state.
 *
 * `isAuthenticated` intentionally remains a boolean because it is part of the
 * broad auth API. Consumers that need to distinguish the optimistic hydration
 * window should use `isReady`, which is false for the server snapshot and first
 * client hydration pass, then true once the client reads `TokenService`.
 */
function useIsAuthenticated(initialIsAuthenticated: boolean) {
  const snapshotRef = useRef<AuthSnapshot>();

  const getAuthSnapshot = useCallback(
    (isAuthenticated: boolean, isReady: boolean) => {
      if (
        snapshotRef.current?.isAuthenticated !== isAuthenticated ||
        snapshotRef.current.isReady !== isReady
      ) {
        snapshotRef.current = { isAuthenticated, isReady };
      }

      return snapshotRef.current;
    },
    [],
  );

  const subscribe = useCallback((onStoreChange: () => void) => {
    tokenService.addEventListener(AUTH_STATUS_CHANGE_EVENT, onStoreChange);

    return () => {
      tokenService.removeEventListener(AUTH_STATUS_CHANGE_EVENT, onStoreChange);
    };
  }, []);

  return useSyncExternalStore(
    subscribe,
    () => getAuthSnapshot(tokenService.isAuthenticated, true),
    () => getAuthSnapshot(initialIsAuthenticated, false),
  );
}

enum RedirectingTo {
  App,
  Login,
}

const REDIRECTING_TO_PATHS: Record<RedirectingTo, string> = {
  [RedirectingTo.App]: "/",
  [RedirectingTo.Login]: "/login",
};

const DEFAULT_POST_LOGIN_PATH = RedirectingTo.App;

export const brandIdLocalStorage = new LocalStorageKey<UUID>("brandId");

const AUTH_PAYLOAD_FIELDS = serverGql(/* ServerGraphQL */ `
  fragment AuthPayloadFields on ObtainJSONWebToken {
    token
    refreshToken
    refreshExpiresIn
  }
`);

type AuthPayloadData = FragmentType<typeof AUTH_PAYLOAD_FIELDS>;

interface AuthContext {
  /**
   * Active brand for brand-scoped queries and integrations. This is masked to
   * `null` when current brand context is not supported so admin and broker
   * views do not accidentally inherit stale brand context.
   */
  currentBrandId: string | null;
  /**
   * Completes an already-successful auth flow by storing tokens, notifying auth
   * subscribers, tracking sign-in, and performing the configured post-login
   * navigation.
   */
  handleLogin: (
    /**
     * Token bundle returned by server auth flows after credentials, signup, or
     * password reset have succeeded. Missing token data is treated as a failed
     * server contract, not as an unauthenticated state.
     */
    authPayload: AuthPayloadData,
    /**
     * Redirect override for flows that need different post-login navigation;
     * omit it for the normal `next`/home behavior.
     */
    options?: UseLoginOptions,
  ) => Promise<void>;
  /**
   * Clears client auth state, brand context, user-tracking integrations, and the
   * Apollo cache after revoking the refresh token, then navigates to login.
   */
  handleLogout: () => Promise<void>;
  /**
   * Whether the auth snapshot is backed by client-side `TokenService` state.
   * Use this before making decisions that cannot rely on the SSR route-policy
   * default.
   */
  isAuthReady: boolean;
  /**
   * Whether the active app mode supports current brand context. Consumers should
   * check this before inferring or selecting a non-null current brand.
   */
  isCurrentBrandSupported: boolean;
  /**
   * Boolean auth state used for broad render and query gating. During SSR and
   * initial hydration this can still be the route-policy default; combine it
   * with `isAuthReady` when actual client auth state is required.
   */
  isAuthenticated: boolean;
  /**
   * Low-level in-memory brand context setter. Prefer `useSetCurrentBrandId` for
   * account switches so local storage, Apollo cache, and ephemeral brand state
   * stay aligned.
   */
  setCurrentBrandId: (
    /** Brand to activate for brand-scoped context, or `null` to clear it. */
    brandId: string | null,
  ) => void;
}

const authContext = createContext<AuthContext>({
  currentBrandId: brandIdLocalStorage.get() || null,
  handleLogin: () => Promise.reject(new Error("Not initialized")),
  handleLogout: () => Promise.reject(new Error("Not initialized")),
  isAuthReady: false,
  isCurrentBrandSupported: true,
  isAuthenticated: false,
  setCurrentBrandId: () => {},
});

interface AuthProviderProps {
  children: React.ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const router = useRouter();
  const client = useApolloClient();
  const authenticatedRedirectPath = getAuthenticatedRedirectPath(
    router.pathname,
  );
  const isProtectedRoute = getIsProtectedPath(router.pathname);
  const { isAuthenticated, isReady: isAuthReady } =
    useIsAuthenticated(isProtectedRoute);
  const [redirectingTo, setRedirectingTo] = useState<RedirectingTo | null>(
    null,
  );
  const redirectingToRef = useRef<RedirectingTo | null>(null);
  const [currentBrandId, setCurrentBrandId] = useState<string | null>(
    brandIdLocalStorage.get() || null,
  );
  const skipNextRedirectRef = useRef(false);

  const { isReady, push, replace, query } = router;

  const redirectTo = useCallback(
    async (to: RedirectingTo, path?: string) => {
      if (redirectingToRef.current === to) return;

      console.log(`redirecting to ${to}`);

      redirectingToRef.current = to;
      setRedirectingTo(to);

      const redirectToPath = path || REDIRECTING_TO_PATHS[to];

      const redirectToUrl = (() => {
        if (to !== RedirectingTo.Login || typeof window === "undefined") {
          return redirectToPath;
        }

        const nextPath = window.location.pathname;

        if (
          !nextPath ||
          nextPath === REDIRECTING_TO_PATHS[RedirectingTo.Login] ||
          nextPath === redirectToPath ||
          nextPath === REDIRECTING_TO_PATHS[DEFAULT_POST_LOGIN_PATH]
        )
          return redirectToPath;

        return {
          pathname: redirectToPath,
          query: {
            next: nextPath + window.location.search + window.location.hash,
          },
        };
      })();

      await replace(redirectToUrl);
      redirectingToRef.current = null;
      setRedirectingTo(null);
    },
    [replace],
  );

  const nextPathFromQuery =
    isReady && authenticatedRedirectPath
      ? getAuthenticatedRedirectNextPath(query?.next)
      : undefined;

  const handleLogin: AuthContext["handleLogin"] = useCallback(
    async (authPayload, { redirectPath: redirectPathOption } = {}) => {
      const auth = getFragmentData(AUTH_PAYLOAD_FIELDS, authPayload);

      if (!auth.token) {
        throw new Error("Login completed but didn't return a `token`.");
      }

      const { token, refreshToken, refreshExpiresIn } = auth;

      skipNextRedirectRef.current = true;
      tokenService.token = token;
      tokenService.setRefreshToken(refreshToken, refreshExpiresIn);

      trackEvent("Signed In");

      const redirectPath = (() => {
        if (typeof redirectPathOption !== "undefined")
          return redirectPathOption;
        if (nextPathFromQuery) return nextPathFromQuery;
        return REDIRECTING_TO_PATHS[DEFAULT_POST_LOGIN_PATH];
      })();

      if (redirectPath) await replace(redirectPath);
    },
    [replace, nextPathFromQuery],
  );

  const handleLogout = useCallback(async () => {
    trackEvent("Signed Out");
    await tokenService.revokeToken();
    skipNextRedirectRef.current = true;
    tokenService.setRefreshToken(undefined);
    tokenService.token = undefined;
    setSegmentBrandContext(null);
    clearCurrentUser();
    await clearUserbackCurrentUser();
    await push("/login");
    await client.clearStore();
  }, [push, client]);

  useEffect(() => {
    // Try to get a token from an existing refresh token, if available. If this
    // fails for an expected auth reason, we'll assume login is required.
    tokenService.getOrRefreshToken().catch((error: Error | undefined) => {
      if (!getIsExpectedAuthRefreshError(error)) logAsyncCatch(error);
    });
  }, []);

  useEffect(() => {
    if (skipNextRedirectRef.current) {
      skipNextRedirectRef.current = false;
      return;
    }

    // Redirect to the configured destination if the user is signed in
    if (authenticatedRedirectPath && isAuthenticated) {
      if (!isReady) return;

      redirectTo(
        RedirectingTo.App,
        nextPathFromQuery || authenticatedRedirectPath,
      ).catch(() => {});
    }

    // Redirect to /login if the protected route is loaded without auth
    if (isProtectedRoute && !isAuthenticated) {
      redirectTo(RedirectingTo.Login).catch(() => {});
    }
  }, [
    isAuthenticated,
    redirectTo,
    authenticatedRedirectPath,
    isProtectedRoute,
    isReady,
    nextPathFromQuery,
  ]);

  const isCurrentBrandSupported =
    getAppModeForPath(router.pathname) === AppMode.BRAND;
  const activeCurrentBrandId = isCurrentBrandSupported ? currentBrandId : null;

  const context = useMemo(
    () => ({
      currentBrandId: activeCurrentBrandId,
      handleLogin,
      handleLogout,
      isAuthReady,
      isCurrentBrandSupported,
      isAuthenticated,
      setCurrentBrandId,
    }),
    [
      activeCurrentBrandId,
      handleLogin,
      handleLogout,
      isAuthReady,
      isCurrentBrandSupported,
      isAuthenticated,
    ],
  );

  const isPendingAuthenticatedRedirect =
    !!authenticatedRedirectPath && isAuthenticated && !isReady;
  const activeRedirectingTo =
    redirectingTo ??
    (isPendingAuthenticatedRedirect ? RedirectingTo.App : null);

  return (
    <authContext.Provider value={context}>
      {children}
      <RedirectingOverlay
        isOpen={activeRedirectingTo !== null}
        header={`Redirecting to ${
          activeRedirectingTo === RedirectingTo.Login ? "Login" : "App"
        }`}
      >
        {activeRedirectingTo === RedirectingTo.Login &&
          "You must be logged in to access this page."}
      </RedirectingOverlay>
    </authContext.Provider>
  );
}

export function useAuth() {
  return useContext(authContext);
}

type UseLoginOptions = {
  /**
   * Post-login destination. Leave undefined to honor a safe `next` query param
   * or fall back to the app home route; pass `null` when the caller needs to
   * keep control of the flow after tokens are stored.
   */
  redirectPath?: string | null;
};

function useIsMounted() {
  const mounted = useRef(false);
  const getIsMounted = useCallback(() => mounted.current, []);

  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);

  return getIsMounted;
}

type LoginResult = {
  /** Will be `true` from the start of the token request, through to the
   * completion of login. */
  isLoading: boolean;
  /** Will be `true` from the successful completion of the token request through
   * to the completion of login. */
  isLoggingIn: boolean;
};

export function useLogin(
  loginOptions?: UseLoginOptions,
): [
  ...MutationTuple<
    ResultOf<typeof authQueries.GET_TOKEN>,
    VariablesOf<typeof authQueries.GET_TOKEN>
  >,
  LoginResult,
] {
  const { handleLogin } = useAuth();
  const [isLoading, setLoading] = useState(false);
  const [isLoggingIn, setLoggingIn] = useState(false);
  const getIsMounted = useIsMounted();

  const [loginMutation, result] = useMutation(authQueries.GET_TOKEN, {
    client: serverClient,
    onError: () => {
      setLoggingIn(false);
    },
  });

  const login = async (options?: Parameters<typeof loginMutation>[0]) => {
    setLoading(true);

    const response = await loginMutation(options);

    const { data, errors } = response;

    if (!data || errors) {
      setLoading(false);
      return response;
    }

    const { auth } = data;
    assertPresence(auth, "Login completed without returning auth data.");

    setLoggingIn(true);

    await handleLogin(auth, loginOptions);

    if (getIsMounted()) {
      setLoading(false);
      setLoggingIn(false);
    }

    return response;
  };

  return [login, result, { isLoading, isLoggingIn }];
}

export function useLogout(): [() => Promise<void>, { loading: boolean }] {
  const { handleLogout: performLogout } = useAuth();
  const [loading, setLoading] = useState(false);
  const getIsMounted = useIsMounted();

  const logout = async () => {
    setLoading(true);
    const result = await performLogout();
    if (getIsMounted()) setLoading(false);
    return result;
  };

  return [logout, { loading }];
}
